import React, { useCallback, useEffect, type FC } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { N400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { AiIcon as AILogo } from '@atlassian/jira-atlassian-intelligence';
import { useIntl } from '@atlassian/jira-intl';
import { useIsModal, useIssueKey } from '@atlassian/jira-issue-context-service';
import { isLeftClickOnly } from '@atlassian/jira-issue-view-common-utils';
import { createFullIssueUrl } from '@atlassian/jira-issue-view-store/src/selectors/breadcrumbs-selector';
import { useRouterActions } from '@atlassian/jira-router';
import { steps, useIssueBreakdown } from '../../context';
import messages from '../../messages';

const SUGGEST_ISSUE_QUERY = 'suggestIssue';
const queryParams = { [SUGGEST_ISSUE_QUERY]: 'true' };

type Props = {
	issueHierarchyLevel?: number | undefined;
};

export const IssueBreakdownButton: FC<Props> = ({ issueHierarchyLevel }) => {
	const [{ currentStep }, { setIssueBreakdownStep, closeIssueBreakdown }] = useIssueBreakdown();
	const isSelected = currentStep !== steps.defaultStep;

	const isModal = useIsModal();
	const issueKey = useIssueKey();
	const { push, replace } = useRouterActions();
	const fullIssueUrl = createFullIssueUrl('', issueKey);
	const query = new URLSearchParams(queryParams);
	const urlWithQuery = `${fullIssueUrl}?${query}`;

	// Redirect to full issue page when on the modal view
	const onModalClick = useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			if (isLeftClickOnly(event)) {
				push(urlWithQuery);
			} else {
				window.open(urlWithQuery, '_blank');
			}
		},
		[push, urlWithQuery],
	);

	// Toggle to open/close issue breakdown
	const onFullViewClick = useCallback(() => {
		if (isSelected) {
			closeIssueBreakdown();
		} else {
			setIssueBreakdownStep(steps.promptInputStep);
		}
	}, [closeIssueBreakdown, isSelected, setIssueBreakdownStep]);

	// If the query string contains suggestIssue=true
	// Start the issue suggesting when mount the component
	useEffect(() => {
		if (!isModal && !isSelected) {
			const params = new URLSearchParams(window.location.search);
			const shouldSuggest = params.has(SUGGEST_ISSUE_QUERY, queryParams[SUGGEST_ISSUE_QUERY]);
			if (shouldSuggest) {
				setIssueBreakdownStep(steps.promptInputStep);
				replace(fullIssueUrl);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const commonProps = {
		spacing: 'compact',
		iconBefore: <AILogo size="medium" label="" />,
		interactionName:
			'auto-generated.issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-button.button.button',
		testId:
			'issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-button.button',
	} as const;

	const { formatMessage } = useIntl();

	const issueNameForFormatting = {
		issueType: issueHierarchyLevel && issueHierarchyLevel > 0 ? 'issues' : 'tasks',
	};

	const buttonLabel = formatMessage(messages.buttonLabel, issueNameForFormatting);

	const buttonDefaultTooltip = formatMessage(
		messages.buttonDefaultTooltipContent,
		issueNameForFormatting,
	);

	const buttonModalViewTooltip = formatMessage(
		messages.buttonModalViewTooltipContent,
		issueNameForFormatting,
	);

	if (isModal) {
		return (
			<Tooltip content={buttonModalViewTooltip} position="right">
				<IssueBreakdownButtonStyled {...commonProps} onClick={onModalClick} href={urlWithQuery}>
					{buttonLabel}
				</IssueBreakdownButtonStyled>
			</Tooltip>
		);
	}

	return (
		<Tooltip content={buttonDefaultTooltip} position="right">
			<IssueBreakdownButtonStyled
				{...commonProps}
				isSelected={isSelected}
				onClick={onFullViewClick}
			>
				{buttonLabel}
			</IssueBreakdownButtonStyled>
		</Tooltip>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueBreakdownButtonStyled = styled(Button)({
	color: token('color.text.subtlest', N400),
});
